import { TranslationKey } from '@structura/i18n'

export type ResponseDiagramType =
  | 'epsilonTop'
  | 'epsilonMid'
  | 'epsilonBot'
  | 'sigmaTop'
  | 'sigmaMid'
  | 'sigmaBot'
  | 'gammaLower'
  | 'gammaMid'
  | 'gammaUpper'
  | 'tauLower'
  | 'tauMid'
  | 'tauUpper'
  | 'urMaxOverall'
  | 'urBending'
  | 'urCompression'
  | 'urTension'
  | 'urShear'
  | 'urSlsDeflection'
  | 'urSlsCrackWidth'
  | 'urSlsConcreteStress'
  | 'urSlsSteelStress'
  | 'slsDeflectionInst'
  | 'slsDeflectionFinal'

// A diagram card. `title` is used for symbol-only labels (σ/ε/τ/γ, language-neutral);
// the UR/SLS cards carry titleKey/descKey for translation.
export interface DiagramOption
{
  id: ResponseDiagramType
  title?: string
  titleKey?: TranslationKey
  description?: string
  descKey?: TranslationKey
}

export const strainStressDiagramOptions: DiagramOption[] = [
  // Left col: stresses, Right col: strains (rendered in 2-col grid)
  { id: 'sigmaTop', title: 'σ Top' },
  { id: 'epsilonTop', title: 'ε Top' },
  { id: 'sigmaMid', title: 'σ Mid' },
  { id: 'epsilonMid', title: 'ε Mid' },
  { id: 'sigmaBot', title: 'σ Bot' },
  { id: 'epsilonBot', title: 'ε Bot' },
  { id: 'tauLower', title: 'τ Lower' },
  { id: 'gammaLower', title: 'γ Lower' },
  { id: 'tauMid', title: 'τ Mid' },
  { id: 'gammaMid', title: 'γ Mid' },
  { id: 'tauUpper', title: 'τ Upper' },
  { id: 'gammaUpper', title: 'γ Upper' }
]

export const ulsDiagramOptions: DiagramOption[] = [
  { id: 'urMaxOverall', titleKey: 'beam.response.urMaxOverallTitle', descKey: 'beam.response.urMaxOverallDesc' },
  { id: 'urBending', titleKey: 'beam.response.urBendingTitle', descKey: 'beam.response.urBendingDesc' },
  { id: 'urShear', titleKey: 'beam.response.urShearTitle', descKey: 'beam.response.urShearDesc' },
  { id: 'urCompression', titleKey: 'beam.response.urCompressionTitle', descKey: 'beam.response.urCompressionDesc' },
  { id: 'urTension', titleKey: 'beam.response.urTensionTitle', descKey: 'beam.response.urTensionDesc' }
]

export const slsDiagramOptions: DiagramOption[] = [
  { id: 'urSlsDeflection', titleKey: 'beam.response.slsDeflectionTitle', descKey: 'beam.response.slsDeflectionDesc' },
  { id: 'slsDeflectionInst', titleKey: 'beam.response.slsDeflectionInstTitle', descKey: 'beam.response.slsDeflectionInstDesc' },
  { id: 'slsDeflectionFinal', titleKey: 'beam.response.slsDeflectionFinalTitle', descKey: 'beam.response.slsDeflectionFinalDesc' },
  { id: 'urSlsCrackWidth', titleKey: 'beam.response.slsCrackWidthTitle', descKey: 'beam.response.slsCrackWidthDesc' },
  { id: 'urSlsConcreteStress', titleKey: 'beam.response.slsConcreteStressTitle', descKey: 'beam.response.slsConcreteStressDesc' },
  { id: 'urSlsSteelStress', titleKey: 'beam.response.slsSteelStressTitle', descKey: 'beam.response.slsSteelStressDesc' }
]

// SLS diagrams whose data follows the SLS combination dropdown (URs plus the mm-valued curves)
export function isSlsResponseDiagram(id: ResponseDiagramType): boolean {
  return id.startsWith('urSls') || id === 'slsDeflectionInst' || id === 'slsDeflectionFinal'
}

// Filter SLS diagram options based on material type. steelStressEvaluated: whether any SLS
// combination in the current response actually evaluated the shared urSlsSteelStress check —
// the reinforcement stress for concrete (EC2 §7.2, Stress_Steel_Quasi) and the opt-in timber
// stress limit (Stress_Timber_Quasi, deliberately not defaulted) — so those cards appear only when
// the project configured the limit (availability-driven, not material-driven).
export function getSlsDiagramOptionsForMaterial(material: string, steelStressEvaluated: boolean): DiagramOption[] {
  const lowerMaterial = material.toLowerCase()

  return slsDiagramOptions.filter(option => {
    // Deflection UR and the winst/wfin mm curves exist for every material: timber (kdef) and
    // concrete (φ, ζ-method) carry creep in wfin; steel has no creep and reports wfin = winst.
    if (option.id === 'urSlsDeflection' || option.id === 'slsDeflectionInst' || option.id === 'slsDeflectionFinal') return true

    // Crack width is concrete-only
    if (option.id === 'urSlsCrackWidth') return lowerMaterial === 'concrete'
    
    // Concrete stress is concrete-only
    if (option.id === 'urSlsConcreteStress') return lowerMaterial === 'concrete'
    
    // Steel always has its stress card; concrete (reinforcement) and timber show it ONLY when the
    // check was actually evaluated in the response (see steelStressEvaluated above).
    if (option.id === 'urSlsSteelStress') {
      if (lowerMaterial === 'steel') return true
      return steelStressEvaluated
    }

    return true
  }).map(option => {
    // Steel does not creep: its w_fin card says so instead of "incl. creep".
    if (option.id === 'slsDeflectionFinal' && lowerMaterial === 'steel') {
      return { ...option, descKey: 'beam.response.slsDeflectionFinalDescSteel' as const }
    }
    // The shared urSlsSteelStress diagram carries the timber stress check for timber models.
    if (option.id === 'urSlsSteelStress' && lowerMaterial === 'timber') {
      return {
        ...option,
        titleKey: 'beam.response.slsTimberStressTitle' as const,
        descKey: 'beam.response.slsTimberStressDesc' as const
      }
    }
    return option
  })
}


